import { ABTests } from '@play-co/gcinstant';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { teaHash } from '@play-co/replicant';
import ab, {
  ABTestID,
  ABTestBucket,
  ReceiverABTestID,
} from 'src/replicant/ruleset/ab';
import StateObserver from 'src/StateObserver';

const random = (seed: string) => {
  let n = 0;
  return () => teaHash(seed, n++);
};

function pickBucketName<T extends ABTestID>(
  test: T,
  buckets: ABTestBucket<T>[],
): ABTestBucket<T> {
  const hash = random(test + StateObserver.getState().user.id);

  const roll = Math.min(
    buckets.length - 1,
    Math.floor(hash() * buckets.length),
  );

  return buckets[roll] as ABTestBucket<T>;
}

let tests: ABTests | null = null;

export const AB = {
  ...ab.tests,

  initialize() {
    platform.abTests?.initialize(ab.config, random, 'buruburuAB');
    tests = platform.abTests!;

    // May not have replicant in tests.
    if (StateObserver.replicant) {
      platform.setExternalABTestsSource(StateObserver.replicant.abTests);
    }
  },

  getBucketID<T extends ABTestID>(test: T): ABTestBucket<T> {
    return tests!.getBucketID(test) as ABTestBucket<T>;
  },

  getReceiverBucketID<T extends ReceiverABTestID>(
    test: T,
    userId: string,
  ): ABTestBucket<T> {
    return tests!.getUserBucketID(test, userId) as ABTestBucket<T>;
  },

  async assignTestManually<T extends ABTestID>(
    test: T,
    bucket?: ABTestBucket<T>,
  ): Promise<ABTestBucket<T>> {
    const existingBucket = tests!.getBucketID(test);

    // Only set it if it doesnt exist or it needs to be updated
    if (existingBucket && (!bucket || bucket === existingBucket)) {
      return existingBucket as ABTestBucket<T>;
    }

    tests!.assignTestManually(test, bucket, true);
    const newBucket = bucket || tests!.getBucketID(test);

    await StateObserver.invoke.addAbTests({
      [test]: newBucket,
    });

    return newBucket as ABTestBucket<T>;
  },

  unassignTest<T extends ABTestID>(test: T) {
    // TODO: move this to GCInstant
    delete tests!.tests?.[test];
    (tests as any).save();
    (tests as any).applyUserProperties();
  },

  isNewPlayer(): boolean {
    return tests!.isNewPlayer;
  },

  async assignSelectiveBucketManually<T extends ABTestID>(
    test: T,
    buckets: string[],
  ): Promise<ABTestBucket<T>> {
    const newBucket = pickBucketName(test, buckets as ABTestBucket<T>[]);

    await AB.assignTestManually(test, newBucket);

    return newBucket as ABTestBucket<T>;
  },
};
